
import React, { useState, useEffect } from 'react';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { CookieBanner } from './components/CookieBanner';
import { Toast } from './components/ui/Toast'; // Importar Toast
import { MabelitaLanding } from './pages/MabelitaLanding';
import { TejiendoFullLanding } from './pages/TejiendoFullLanding';
import { Singer } from './pages/Singer';
import { Merch } from './pages/Merch';
import { Auth } from './pages/Auth';
import { Checkout } from './pages/Checkout';
import { Success } from './pages/Success';
import { Cancel } from './pages/Cancel';
import { Account } from './pages/Account';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { ResetPassword } from './pages/ResetPassword';
import { Cookies } from './pages/Cookies';
import { TejiendoLanding } from './pages/TejiendoLanding';
import { TunantadaLanding } from './pages/TunantadaLanding';
import { ShoppingLanding } from './pages/ShoppingLanding';
import { Product } from './types';
import { supabase } from './services/supabaseClient';
import { DatabaseStatus } from './components/DatabaseStatus';
import { Helmet } from 'react-helmet-async';

const App: React.FC = () => {
  // --- SUBDOMAIN TENANT DETECTION ---
  // Utilizamos '127.0.0.1' como simulador de la tienda para el entorno de desarrollo local
  const isStorefront = window.location.hostname.includes('tejiendocon') || window.location.hostname === '127.0.0.1';

  const [currentPage, setCurrentPage] = useState(isStorefront ? 'tejiendo' : 'home');
  const [session, setSession] = useState<any>(null);
  const [siteConfig, setSiteConfig] = useState<any>({}); // Init empty obj
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  // --- TOAST STATE ---
  const [toast, setToast] = useState<{ message: string, type: 'success' | 'error' | 'info' } | null>(null);

  // --- THEME ENGINE (Motor de Diseño) ---
  // Helper: check if current user is admin
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    if (!session?.user) { setIsAdmin(false); return; }
    supabase.from('profiles').select('role').eq('id', session.user.id).single()
      .then(({ data }) => setIsAdmin(data?.role === 'admin'));
  }, [session]);

  useEffect(() => {
    const CACHE_KEY = 'mab_site_config';
    const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

    const applyTheme = async (forceRefresh = false) => {
      try {
        let configMap: any = {};
        try {
          // Intentar cache primero (ahorra egress)
          let rawConfig: any = null;
          if (!forceRefresh) {
            try {
              const cached = localStorage.getItem(CACHE_KEY);
              if (cached) {
                const { data: cachedData, ts } = JSON.parse(cached);
                if (Date.now() - ts < CACHE_TTL) {
                  rawConfig = cachedData;
                }
              }
            } catch {} // localStorage puede fallar en incógnito
          }

          // Si no hay cache válido, fetch desde Supabase
          if (!rawConfig) {
            const { data, error } = await supabase.from('site_config').select('key, value');
            if (error) {
              console.warn("Error fetching theme (using defaults):", error);
            } else {
              rawConfig = data ? data.reduce((acc: any, curr) => ({ ...acc, [curr.key]: curr.value }), {}) : {};
              // Guardar en cache
              try {
                localStorage.setItem(CACHE_KEY, JSON.stringify({ data: rawConfig, ts: Date.now() }));
              } catch {}
            }
          }

          if (rawConfig) {
            if (isStorefront) {
              const weaverConfig: any = {};
              Object.keys(rawConfig).forEach(key => {
                if (key.startsWith('tej_')) {
                  weaverConfig[key.replace('tej_', '')] = rawConfig[key];
                }
              });
              if (rawConfig.tejiendo_logo_url) weaverConfig.logo_url = rawConfig.tejiendo_logo_url;
              if (rawConfig.logo_url) weaverConfig.tejiendo_logo_url = rawConfig.logo_url;
              configMap = weaverConfig;
            } else {
              const singerConfig: any = {};
              Object.keys(rawConfig).forEach(key => {
                if (!key.startsWith('tej_')) {
                  singerConfig[key] = rawConfig[key];
                }
              });
              configMap = singerConfig;
            }
          }
        } catch (err) {
          console.warn("Supabase connection error or missing table:", err);
        }

        setSiteConfig(configMap);

        const root = document.documentElement;

        // 2. APLICAR FUENTES
        const fontPairs: any = {
          classic: { serif: "'Playfair Display', serif", sans: "'Inter', sans-serif" },
          modern: { serif: "'Montserrat', sans-serif", sans: "'Lato', sans-serif" },
          elegant: { serif: "'Merriweather', serif", sans: "'Lato', sans-serif" }
        };

        const selectedFont = fontPairs[configMap.font_pair || 'classic'];
        root.style.setProperty('--font-serif', selectedFont.serif);
        root.style.setProperty('--font-sans', selectedFont.sans);

        // 3. APLICAR PALETA DE COLORES
        const palettes: any = {
          earth: {
            primary: '#A0522D', secondary: '#8B4513', accent: '#D2691E', light: '#F5F1E8', dark: '#2C1810'
          },
          minimal: {
            primary: '#1a1a1a', secondary: '#4a4a4a', accent: '#9e9e9e', light: '#ffffff', dark: '#000000'
          },
          vibrant: {
            primary: '#E63946', secondary: '#1D3557', accent: '#457B9D', light: '#F1FAEE', dark: '#1D3557'
          },
          custom: {
            primary: configMap.color_primary,
            secondary: configMap.color_secondary,
            light: configMap.color_light,
            dark: configMap.color_dark
          }
        };

        const activePalette = palettes[configMap.color_palette] || palettes.earth;

        root.style.setProperty('--color-primary', activePalette.primary);
        root.style.setProperty('--color-secondary', activePalette.secondary);
        root.style.setProperty('--color-accent', activePalette.accent);
        root.style.setProperty('--color-light', activePalette.light);
        root.style.setProperty('--color-dark', activePalette.dark);

      } catch (e) {
        console.error("Theme engine error:", e);
      }
    };

    applyTheme();

    // REALTIME: Solo abrir websocket para admin (ahorra ~2-3 GB egress/mes)
    let channel: any = null;
    if (isAdmin) {
      channel = supabase
        .channel('theme_updates')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'site_config' },
          (payload) => {
            console.log("Cambio de tema detectado en tiempo real!", payload);
            // Invalidar cache y recargar
            try { localStorage.removeItem(CACHE_KEY); } catch {}
            applyTheme(true);
          }
        )
        .subscribe();
    }

    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, [isAdmin]);

  // --- CART LOGIC ---
  const [cart, setCart] = useState<Product[]>([]);
  const [cartLoaded, setCartLoaded] = useState(false);

  useEffect(() => {
    const fetchCart = async () => {
      if (session?.user) {
        try {
          const { data, error } = await supabase.from('profiles').select('cart_state').eq('id', session.user.id).single();
          if (!error && data?.cart_state?.items) {
            setCart(data.cart_state.items);
          }
        } catch (err) {
          // column cart_state may not exist yet
        }
      } else {
        setCart([]); // Invitados no guardan estado persistente por ahora
      }
      setCartLoaded(true);
    };
    if (session !== null) {
      fetchCart();
    } else {
      setCartLoaded(true);
    }
  }, [session]);

  useEffect(() => {
    if (session?.user && cartLoaded) {
      const syncCart = async () => {
        try {
          const { data, error } = await supabase.from('profiles').select('cart_state').eq('id', session.user.id).single();
          if (!error) {
            const currentState = data?.cart_state || {};
            await supabase.from('profiles').update({
              cart_state: { ...currentState, items: cart }
            }).eq('id', session.user.id);
          }
        } catch (err) { }
      };
      syncCart();
    }
  }, [cart, session, cartLoaded]);

  const addToCart = (product: Product) => {
    setCart((prevCart) => [...prevCart, product]);
    // Trigger Toast
    setToast({ message: `¡${product.name} añadido al carrito!`, type: 'success' });
  };

  const clearCart = () => {
    setCart([]);
    if (session?.user) {
      const clearSync = async () => {
        try {
          const { data, error } = await supabase.from('profiles').select('cart_state').eq('id', session.user.id).single();
          if (!error) {
            const currentState = data?.cart_state || {};
            await supabase.from('profiles').update({
              cart_state: { ...currentState, items: [] }
            }).eq('id', session.user.id);
          }
        } catch (err) { }
      };
      clearSync();
    }
  };

  // --- AUTH LOGIC ---
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    }).catch((err) => {
      console.warn("Supabase auth session failed (likely network or config error):", err);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  // --- ROUTING ---
  useEffect(() => {
    const handleHashChange = () => {
      // Obtenemos el hash completo sin el primer # (ej: "singer" o "singer#gallery")
      const fullHash = window.location.hash.replace('#', '') || (isStorefront ? 'tejiendo' : 'home');

      // Extraemos la página principal (ej: "singer")
      // Si usamos anclas dobles como #singer#gallery, split tomamos el primero
      const page = fullHash.split('#')[0] || (isStorefront ? 'tejiendo' : 'home');

      setCurrentPage(page);

      // Manejo de scroll suave si hay un segundo hash (ancla interna)
      const anchorId = fullHash.split('#')[1];
      if (anchorId) {
        setTimeout(() => {
          const element = document.getElementById(anchorId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100); // Pequeño delay para asegurar que la página renderizó
      } else {
        window.scrollTo(0, 0);
      }
    };

    window.addEventListener('hashchange', handleHashChange);

    // Ejecutar al inicio
    const initialHash = window.location.hash.replace('#', '');
    if (initialHash) {
      const page = initialHash.split('#')[0];
      setCurrentPage(page || (isStorefront ? 'tejiendo' : 'home'));
    } else if (isStorefront) {
      setCurrentPage('tejiendo');
    }

    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const navigate = (page: string) => {
    const currentHash = window.location.hash.replace('#', '');
    if (currentHash === page) {
      // Same page — hashchange won't fire, so manually scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setCurrentPage(page);
    } else {
      window.location.hash = page;
    }
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <MabelitaLanding onNavigate={navigate} siteConfig={siteConfig} />;
      case 'singer':
        return <MabelitaLanding onNavigate={navigate} siteConfig={siteConfig} />;
      case 'tienda':
        return <Merch addToCart={addToCart} siteConfig={siteConfig} defaultType={isStorefront ? "tejidos" : "all"} lockedType={isStorefront ? "tejidos" : undefined} isStorefront={isStorefront} />;
      case 'tejiendo':
        return <TejiendoFullLanding siteConfig={siteConfig} />;
      case 'encargos':
        return <TejiendoFullLanding siteConfig={siteConfig} />;
      case 'shopping-mabelita':
        return isStorefront
          ? <Merch addToCart={addToCart} siteConfig={siteConfig} defaultType="tejidos" lockedType="tejidos" isStorefront={isStorefront} />
          : <ShoppingLanding addToCart={addToCart} siteConfig={siteConfig} />;
      case 'tunantada':
        return isStorefront
          ? <Merch addToCart={addToCart} siteConfig={siteConfig} defaultType="tejidos" lockedType="tejidos" isStorefront={isStorefront} />
          : <TunantadaLanding addToCart={addToCart} siteConfig={siteConfig} />;
      case 'auth':
        return (
          <>
            <Helmet><title>{`Iniciar Sesión | ${isStorefront ? 'Shopping con Mabelita' : 'Mabelita'}`}</title></Helmet>
            <Auth onLoginSuccess={() => navigate(isStorefront ? 'tejiendo' : 'home')} />
          </>
        );
      case 'reset-password':
        return (
          <>
            <Helmet><title>Recuperar Contraseña | Mabelita</title></Helmet>
            <ResetPassword onSuccess={() => navigate('auth')} />
          </>
        );
      case 'cookies':
        return (
          <>
            <Helmet><title>Política de Cookies | Mabelita</title></Helmet>
            <Cookies onNavigate={navigate} />
          </>
        );
      case 'checkout':
        return (
          <>
            <Helmet><title>Finalizar Compra | Tienda Mabelita</title></Helmet>
            <Checkout cart={cart} setCart={setCart} clearCart={clearCart} onNavigate={navigate} session={session} />
          </>
        );
      case 'success':
        return (
          <>
            <Helmet><title>Pago Exitoso | Mabelita</title></Helmet>
            <Success clearCart={clearCart} onNavigate={navigate} />
          </>
        );
      case 'cancel':
        return (
          <>
            <Helmet><title>Pago Cancelado | Mabelita</title></Helmet>
            <Cancel onNavigate={navigate} />
          </>
        );
      case 'account':
        return session ? (
          <>
            <Helmet><title>Mi Cuenta | Mabelita</title></Helmet>
            <Account session={session} onNavigate={navigate} />
          </>
        ) : <Auth onLoginSuccess={() => navigate('account')} />;
      case 'cart':
        const groupedCart = cart.reduce((acc: any[], item) => {
          const existing = acc.find(i => i.id === item.id);
          if (existing) {
            existing.quantity += 1;
          } else {
            acc.push({ ...item, quantity: 1 });
          }
          return acc;
        }, []);

        const removeFromCartHelper = (productId: number) => {
          const indexToRemove = cart.findIndex(i => i.id === productId);
          if (indexToRemove !== -1) {
            setCart(cart.filter((_, i) => i !== indexToRemove));
          }
        };

        const addToCartHelper = (productId: number) => {
          const itemToAdd = cart.find(i => i.id === productId);
          if (itemToAdd) {
            setCart([...cart, itemToAdd]);
          }
        };

        return (
          <div className="pt-32 text-center min-h-screen px-4 bg-mabelita-light">
            <Helmet><title>{`Carrito (${cart.length}) | Tienda Mabelita`}</title></Helmet>
            <h1 className="text-3xl font-serif mb-4">Tu Carrito ({cart.length})</h1>
            {cart.length === 0 ? (
              <div className="bg-white p-8 rounded-xl shadow-sm max-w-md mx-auto">
                <p className="text-gray-500 mb-4">El carrito está vacío</p>
                <button onClick={() => navigate('shop')} className="text-mabelita-primary font-bold hover:underline">Ir a comprar</button>
              </div>
            ) : (
              <div className="max-w-md mx-auto">
                <ul className="text-left bg-white p-6 rounded-xl shadow-sm border border-gray-100 mb-6">
                  {groupedCart.map((item, i) => (
                    <li key={i} className="border-b py-3 flex flex-col gap-3 last:border-0">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-3">
                          <img src={item.image_url} className="w-12 h-12 rounded object-cover" alt={item.name} />
                          <div>
                            <span className="block font-medium text-gray-900">{item.name}</span>
                            <span className="text-xs text-gray-500">
                              €{(item.price || 0).toFixed(2)} c/u
                            </span>
                          </div>
                        </div>
                        <span className="font-bold text-gray-900">
                          €{((item.price || 0) * item.quantity).toFixed(2)}
                        </span>
                      </div>

                      <div className="flex justify-between items-center mt-1">
                        <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                          <button onClick={() => removeFromCartHelper(item.id)} className="px-3 py-1 bg-gray-50 text-gray-600 hover:bg-gray-100 transition-colors">-</button>
                          <span className="px-3 font-medium text-sm border-x border-gray-200">{item.quantity}</span>
                          <button onClick={() => addToCartHelper(item.id)} className="px-3 py-1 bg-gray-50 text-gray-600 hover:bg-gray-100 transition-colors">+</button>
                        </div>
                        <button onClick={() => setCart(cart.filter(c => c.id !== item.id))} className="text-red-500 text-xs font-semibold hover:underline">Eliminar artículo</button>
                      </div>
                    </li>
                  ))}
                  <li className="mt-4 pt-4 border-t font-bold flex justify-between text-xl">
                    <span>Total</span>
                    <span>€{cart.reduce((a, b) => a + (b.price || 0), 0).toFixed(2)}</span>
                  </li>
                </ul>
                <button
                  onClick={() => navigate('checkout')}
                  disabled={cart.length === 0}
                  className="w-full bg-mabelita-primary text-white py-3 rounded-full font-bold hover:opacity-90 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Proceder al Pago Seguro
                </button>
              </div>
            )}
          </div>
        );
      case 'admin':
        return session ? <AdminDashboard session={session} /> : <Auth onLoginSuccess={() => navigate('admin')} />;
      default:
        return isStorefront
          ? <TejiendoFullLanding siteConfig={siteConfig} />
          : <MabelitaLanding onNavigate={navigate} siteConfig={siteConfig} />;
    }
  };

  return (
    <React.Fragment>
      <DatabaseStatus />
      <div className="font-sans text-mabelita-dark min-h-screen flex flex-col bg-mabelita-light transition-colors duration-500">
        {/* Navbar */}
        {currentPage === 'admin' || isCheckingOut ? null : (
          <Navbar
            cartCount={cart.reduce((total, item) => total + (item.quantity || 1), 0)}
            onNavigate={navigate}
            currentPage={currentPage}
            session={session}
            themeLayout={siteConfig?.theme_layout}
            logoPosition={siteConfig?.nav_logo_position}
            logoUrl={siteConfig?.logo_url}
            tejiendoLogoUrl={siteConfig?.tejiendo_logo_url}
            isStorefront={isStorefront}
            siteConfig={siteConfig}
          />
        )}
        <main className="flex-grow">
          {renderPage()}
        </main>
        {/* FOOOOTER */}
        {currentPage !== 'admin' && !isCheckingOut && <Footer />}
        <CookieBanner />
        {/* TOAST COMPONENT */}
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      </div>
    </React.Fragment>
  );
};

export default App;
